import React, { useMemo, useState } from "react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import CaseStudyCard from "../components/CaseStudyCard";
import BigCTA from "../components/BigCTA";
import { WORKS } from "../data/content";

const SERVICE_FILTERS = [
  "Impact Strategy & Advisory",
  "Sponsorship & Impact Programs",
  "Content & IP Studio",
  "PitchPartner",
  "Research & Insight",
  "Performance Marketing & Growth",
];

const PILLAR_FILTERS = ["Business Impact", "Cultural Impact", "Social Impact"];

const Works = () => {
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedPillars, setSelectedPillars] = useState([]);

  const toggle = (list, value, setter) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const resetFilters = () => {
    setSelectedServices([]);
    setSelectedPillars([]);
  };

  const hasActiveFilters = selectedServices.length > 0 || selectedPillars.length > 0;

  const items = useMemo(() => {
    return WORKS.filter((w) => {
      const matchService =
        selectedServices.length === 0 ||
        (w.services && w.services.some((s) => selectedServices.includes(s)));
      const matchPillar =
        selectedPillars.length === 0 ||
        (w.pillar && selectedPillars.includes(w.pillar)) ||
        (w.pillarsSecondary && w.pillarsSecondary.some((p) => selectedPillars.includes(p)));
      return matchService && matchPillar;
    });
  }, [selectedServices, selectedPillars]);

  const chipClass = (active) =>
    `px-4 py-2 border text-sm md:text-[15px] font-condensed uppercase tracking-widest transition-colors ${
      active
        ? "bg-lime text-dodgers border-lime"
        : "border-mandrogni/30 text-mandrogni hover:border-lime"
    }`;

  return (
    <>
      <Seo
        title="19 case study sport: AC Milan, Nike, Prada, Mizuno | OnSports"
        description="Il portfolio OnSports: 19 case study per AC Milan, Nike × JD Sports, Prada × America's Cup, Mizuno, Reale Mutua, Credem Banca, Sportlinx360 e altri brand sportivi."
        path="/works"
      />

      <section className="bg-dodgers text-galactico pt-40 pb-24 md:pt-52 md:pb-32" data-testid="works-hero">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Works</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <h1 className="display-h1 mt-8 max-w-[20ch]">
              Non promesse.<br />
              <span className="text-lime">Risultati documentati.</span>
            </h1>
          </RevealOnScroll>
          <RevealOnScroll delay={0.2}>
            <p className="mt-8 body-lg opacity-85 max-w-[60ch]">
              Una selezione di progetti che mostra il nostro approccio: KPI in apertura,
              metodologia replicabile, scorecard finale.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      <section className="bg-galactico text-dodgers pt-12 md:pt-20" data-testid="works-filters">
        <div className="container-os space-y-6">
          {/* Gruppo A — Servizio */}
          <div>
            <p
              className="font-condensed uppercase tracking-widest text-[13px] text-mandrogni mb-3"
              data-testid="filters-services-label"
            >
              Filtra per servizio:
            </p>
            <div className="flex flex-wrap gap-2 md:gap-3">
              {SERVICE_FILTERS.map((s) => {
                const active = selectedServices.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggle(selectedServices, s, setSelectedServices)}
                    className={chipClass(active)}
                    data-testid={`filter-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    aria-pressed={active}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Gruppo B — Pillar */}
          <div>
            <p
              className="font-condensed uppercase tracking-widest text-[13px] text-mandrogni mb-3"
              data-testid="filters-pillars-label"
            >
              Filtra per pillar:
            </p>
            <div className="flex flex-wrap gap-2 md:gap-3">
              {PILLAR_FILTERS.map((p) => {
                const active = selectedPillars.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => toggle(selectedPillars, p, setSelectedPillars)}
                    className={chipClass(active)}
                    data-testid={`filter-pillar-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    aria-pressed={active}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contatore + Reset */}
          <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
            <p className="font-condensed uppercase tracking-widest text-[13px] text-mandrogni" data-testid="filters-count">
              {items.length} case stud{items.length === 1 ? "y" : "y"} mostrati
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="font-condensed uppercase tracking-widest text-[13px] text-dodgers underline-offset-4 hover:underline transition"
                data-testid="filters-reset-btn"
              >
                Reset filtri →
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="section-pad bg-galactico text-dodgers" data-testid="works-grid">
        <div className="container-os">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {items.map((w, i) => (
              <RevealOnScroll key={`${w.title}-${i}`} delay={i * 0.04}>
                <CaseStudyCard
                  title={w.title}
                  category={w.category}
                  kpis={w.kpis}
                  tone={w.tone}
                  to={w.to || "/works"}
                  image={w.image}
                  testId={`work-${i}`}
                />
              </RevealOnScroll>
            ))}
          </div>
          {items.length === 0 && (
            <p className="opacity-70 text-center py-20" data-testid="filters-empty">
              Nessun progetto per questa combinazione di filtri.
            </p>
          )}
        </div>
      </section>

      <BigCTA />
    </>
  );
};

export default Works;
